import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Edit, Loader2, Mail, Phone, MapPin, Briefcase } from 'lucide-react';
import { api } from '../../lib/api';
import ClientAIBriefCard from '../../components/admin/ClientAIBriefCard';
import PresentTalentsCard from '../../components/admin/PresentTalentsCard';

const ClientProfilePage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/clients/${clientId}`);
      const data = await response.json();
      setClient(data);
    } catch (error) {
      console.error('Failed to fetch client:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">Client not found</p>
      </div>
    );
  }

  const activeMissions = client.missions?.filter(m => ['open', 'assigned', 'active'].includes(m.status)) || [];
  const completedMissions = client.missions?.filter(m => m.status === 'completed') || [];

  return (
    <div className="p-4 md:p-8" data-testid="client-profile-page">
      <div className="mb-6">
        <Link to="/admin/clients" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour aux clients
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-display font-semibold mb-1 md:mb-2 truncate">{client.company_name}</h1>
            <p className="text-muted-foreground">{client.contact_name}</p>
          </div>
          <Button variant="outline" size="sm" className="w-fit">
            <Edit className="h-4 w-4 mr-2" />
            Modifier
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4 md:space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-auto min-w-full md:w-auto">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="missions" className="text-xs sm:text-sm whitespace-nowrap">Missions</TabsTrigger>
            <TabsTrigger value="employees" className="text-xs sm:text-sm whitespace-nowrap">Employés</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4 md:space-y-6">
          {/* 1. RÉSUMÉ IA — toujours en premier */}
          <ClientAIBriefCard clientId={clientId} />

          {/* 2. Coordonnées + 3. Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Card className="p-4 md:p-6">
              <h3 className="text-lg font-display font-semibold mb-4">Coordonnées</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{client.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{client.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{client.country}</span>
                </div>
                {client.industry && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{client.industry}</span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-display font-semibold mb-4">Statistiques rapides</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Missions actives</span>
                  <Badge>{activeMissions.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Missions terminées</span>
                  <Badge variant="secondary">{completedMissions.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Talents assignés</span>
                  <Badge>{client.active_assignments?.length || 0}</Badge>
                </div>
              </div>
            </Card>
          </div>

          {/* 4. Notes admin */}
          {client.notes && (
            <Card className="p-6">
              <h3 className="text-lg font-display font-semibold mb-4">Notes</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{client.notes}</p>
            </Card>
          )}

          {/* 5. Talents présentés */}
          <PresentTalentsCard clientId={clientId} />
        </TabsContent>

        <TabsContent value="missions" className="space-y-4">
          {activeMissions.length > 0 && (
            <div>
              <h3 className="text-lg font-display font-semibold mb-4">Active Missions</h3>
              <div className="space-y-3">
                {activeMissions.map((mission) => (
                  <Link key={mission.id} to={`/admin/missions/${mission.id}`}>
                    <Card className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{mission.role_required}</p>
                          <p className="text-sm text-muted-foreground">{mission.seniority_level}</p>
                        </div>
                        <Badge className="capitalize">{mission.status}</Badge>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {completedMissions.length > 0 && (
            <div>
              <h3 className="text-lg font-display font-semibold mb-4">Completed Missions</h3>
              <div className="space-y-3">
                {completedMissions.map((mission) => (
                  <Link key={mission.id} to={`/admin/missions/${mission.id}`}>
                    <Card className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{mission.role_required}</p>
                          <p className="text-sm text-muted-foreground">{mission.seniority_level}</p>
                        </div>
                        <Badge variant="secondary">Completed</Badge>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {client.missions?.length === 0 && (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No missions yet</p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="employees" className="space-y-4">
          {client.active_assignments?.length > 0 ? (
            <div className="space-y-3">
              {client.active_assignments.map((assignment) => (
                <Card key={assignment.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{assignment.employee_name}</p>
                      <p className="text-sm text-muted-foreground">{assignment.employee_role}</p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No employees assigned yet</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientProfilePage;
